import { nextTick } from 'vue';
import { isEmpty } from 'lodash';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import { KeyboardEvent, KeyCode } from '/@/modules/core/ui/ui.constants';
import { BaseUIService } from '/@/modules/core/ui/services/BaseUIService';

export class KeyboardAdapter extends BaseUIService {
  /**
   * @type {Object.<string, number>}
   */
  listeners;

  /**
   * @type {Object.<string, number>}
   */
  disabledListeners;

  constructor() {
    super();
    this.listeners = {};
    this.disabledListeners = {};
    this.init();
  }

  disable(event) {
    this.disabledListeners[event] = true;
  }

  enable(event) {
    this.disabledListeners[event] = false;
  }

  isEventDisabled(keyEvent) {
    return !!this.disabledListeners[keyEvent.name];
  }

  /**
   * Registers subscriber to desired keyboard event
   * @param keyboardEvent
   * @param cb
   */
  // eslint-disable-next-line no-unused-vars
  on(keyboardEvent, cb) {
    this.add(keyboardEvent);
    EventBus.listen(keyboardEvent, cb);
  }

  /**
   * Unregisters subscriber from desired keyboard event
   * @param keyboardEvent
   * @param cb
   */
  // eslint-disable-next-line no-unused-vars
  off(keyboardEvent, cb) {
    this.remove(keyboardEvent);
    EventBus.destroy(keyboardEvent, cb);
  }

  /**
   * Sets up keyboard events
   */
  init() {
    document.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  /**
   * Trigger manually a keyboard event
   * @param eventKey
   */
  trigger(eventKey) {
    const keyEvent = KeyboardEvent.getEventsMetadata(eventKey);
    this.sendEvent({ keyEvent });
  }

  /**
   * @param {Event} event
   */
  onKeyUp(event) {
    return event;
  }

  /**
   * @param {Event} event
   */
  onKeyDown(event) {
    if (!this.hasListeners() || event.repeat) {
      return;
    }

    const keyEvents = KeyCode.getValues().reduce((accum, keyCodeEnum) => {
      if (keyCodeEnum.metaKey === event.metaKey && keyCodeEnum.value === event.keyCode) {
        accum.push(keyCodeEnum);
      }

      return accum;
    }, []);

    keyEvents.forEach((keyEvent) => {
      if (keyEvent?.name in this.listeners) {
        this.sendEvent({ keyEvent, event });
      }
    });
  }

  /**
   * @param {{}} keyEvent
   * @param {Event} event
   * @private
   */
  sendEvent({ keyEvent, event = null }) {
    if (this.isEventDisabled(keyEvent)) {
      return;
    }

    if (
      keyEvent.completeOverride ||
      (keyEvent.override && !document.querySelectorAll(':focus').length)
    ) {
      if (window.event) {
        window.event.cancelBubble = true;
      }
      event?.preventDefault();
      event?.stopPropagation();
    }

    nextTick(() => {
      EventBus.signal(keyEvent.name, event);
    });
  }

  /**
   * @returns {boolean}
   */
  hasListeners() {
    return !isEmpty(this.listeners);
  }

  /**
   * @param keyEvent
   * @returns {boolean}
   */
  hasListener(keyEvent) {
    return !!this.listeners[keyEvent];
  }

  /**
   * @private
   *
   * Increase listener count
   */
  add(keyboardEvent) {
    if (!this.listeners[keyboardEvent]) {
      this.listeners[keyboardEvent] = 0;
    }
    this.listeners[keyboardEvent] += 1;
  }

  /**
   * @private
   *
   * Reduce listener count
   */
  remove(keyboardEvent) {
    this.listeners[keyboardEvent] -= 1;
  }
}

export default new KeyboardAdapter();
